import { Button } from '@/components/button'
import { Trash } from '@phosphor-icons/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CampaignTableItem } from '../pages/campaign/list'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCampaignClient } from '../lib/api'

const DeleteCampaignModal = ({
  campaignToDelete,
}: {
  campaignToDelete: CampaignTableItem
}) => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)
  const queryClient = useQueryClient()
  const campaignApi = useCampaignClient()
  const deleteCampaign = useMutation({
    mutationFn: async (campaignId: string) => {
      return await campaignApi.deleteCampaign.mutate({
        params: { id: campaignId },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })
  const ref = useRef<HTMLDivElement>(null)
  const onClickDelete = () => {
    deleteCampaign.mutate(campaignToDelete.id, {
      onSuccess: () => {
        setShowModal(false)
      },
    })
  }
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShowModal(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])
  const portal = useMemo(() => {
    if (!showModal) return <></>
    return (
      <div
        className="absolute h-screen"
        data-testid="create-incentive-item-form"
      >
        <div className="fixed top-0 left-0 z-30 w-full h-screen bg-ui-100 opacity-10"></div>
        <div className="flex justify-center fixed top-60 left-0 z-40 w-full h-full">
          <div
            ref={ref}
            className="flex flex-col absolute gap-6 p-6 z-50 bg-white rounded-md shadow-md w-[500px] h-fit overflow-y-scroll"
          >
            <div>
              <div className="flex flex-row items-center">
                <Trash size={38} fill="#1812FF" className="mr-4" />
                <div className="gap-1">
                  <p className="text-lg text-gray-9 font-bold pb-1">
                    {t('delete_campaign')} {campaignToDelete.name}
                  </p>
                  <p className="text-md text-gray-10 font-medium py-2">
                    {t('are_you_sure')}
                  </p>
                  <p className="text-sm text-gray-10">
                    {t('deleting_campaign')}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-row justify-end gap-2">
              <Button
                label={t('cancel')}
                onClick={() => setShowModal(false)}
                className="text-primary border border-primary hover:opacity-50"
              />
              <Button
                label={t('delete')}
                onClick={onClickDelete}
                primary={true}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }, [campaignToDelete, showModal])

  return (
    <>
      {portal}
      <button
        onClick={() => setShowModal(true)}
        className="text-sm flex flex-row gap-3 items-center border border-border rounded-md p-1 w-full !text-foreground"
      >
        <Trash size={16} fill="#490091" />
        {t('delete')}
      </button>
    </>
  )
}

export default DeleteCampaignModal
